import React, { useState, useMemo } from 'react';
import { Flex, Icon, Autocomplete, TextField, Spinner } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import {
  useSearchProjects,
  useGetProjectLibraryEntriesByIds,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

interface SingleProjectAutoCompleteProps {
  projectId: string;
  onProjectIdChange: (projectId: string) => void;
  disabled?: boolean;
}

export const SingleProjectAutoComplete = ({
  projectId,
  onProjectIdChange,
  disabled = false,
}: SingleProjectAutoCompleteProps) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebouncedValue(searchText, 500);

  const { data: allProjects = [], isFetching: isFetchingAllProjects } = useSearchProjects({
    searchText: debouncedSearchText,
  });

  const { data: projectLibraryEntries, isFetching: isFetchingProjectLibraryEntries } =
    useGetProjectLibraryEntriesByIds(projectId ? [projectId] : []);

  const selectedProjects = useMemo(
    () =>
      projectLibraryEntries?.libraryEntries?.map(entry => ({
        id: entry.project.id,
        name: entry.project.name,
      })) ?? [],
    [projectLibraryEntries],
  );

  const formattedProjects = (allProjects || []).map(project => ({
    id: project.id,
    name: project.name,
  }));

  // TODO: Once Radix is migrated to 1.36, we can use the AsyncItems and AsyncValues to replace the loading state
  return (
    <Autocomplete.Root
      search={searchText}
      onSearchChange={setSearchText}
      value={projectId}
      onValueChange={(newValue: string) => {
        onProjectIdChange(newValue);
      }}
      disabled={disabled}
    >
      <Autocomplete.Content>
        <Autocomplete.TextField
          placeholder="Search by project name or id..."
          // Do not allow invalid values from paste
          onPaste={() => {}}
        >
          <TextField.Slot side="left">
            <Icon icon={faSearch} label="Search" />
          </TextField.Slot>
          <Autocomplete.Values>
            {projectId.length > 0 && (
              <Autocomplete.Value value={projectId}>
                {isFetchingProjectLibraryEntries || selectedProjects.length === 0 ? (
                  <Spinner />
                ) : (
                  selectedProjects[0].name
                )}
              </Autocomplete.Value>
            )}
          </Autocomplete.Values>
          <Autocomplete.Reset disabled={disabled} />
        </Autocomplete.TextField>
        <Autocomplete.Items>
          {isFetchingAllProjects
            ? [
                <Autocomplete.Item
                  key={'fetching-projects'}
                  keywords={searchText}
                  value={searchText}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <Flex direction="row" gap="2">
                    <Spinner />
                    {'Fetching projects...'}
                  </Flex>
                </Autocomplete.Item>,
              ]
            : formattedProjects.map(project => (
                <Autocomplete.Item key={project.id} value={project.id}>
                  {project.name} ({project.id})
                </Autocomplete.Item>
              ))}
        </Autocomplete.Items>
      </Autocomplete.Content>
    </Autocomplete.Root>
  );
};
